import React, { useState, useEffect } from "react";
import {
  usePrograms,
  useUpdateProgram,
  useToggleVisibility,
  useProgramsByPriority,
  useBulkUpdatePriorities,
} from "../hooks/usePrograms";
import { useLocalStorage } from "../hooks/useLocalStorage";

import ProgramForm from "../components/programs/ProgramForm";
import ProgramDetailModal from "../components/programs/ProgramDetailModal";
import ProgramStats from "../components/programs/ProgramStats";
import SearchBar from "../components/common/SearchBar";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Pagination from "../components/common/Pagination";
import SortableProgramRow from "../components/programs/SortableProgramRow";
import { Program, UpdateProgramData } from "../services/api";
import { FunnelIcon, Bars3BottomLeftIcon } from "@heroicons/react/24/outline";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const Programs: React.FC = () => {
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProgramForDetail, setSelectedProgramForDetail] =
    useState<Program | null>(null);
  const [filter, setFilter] = useState<
    "all" | "active" | "hidden" | "setup-needed"
  >("all");
  const [searchQuery, setSearchQuery] = useLocalStorage<string>(
    "programs-search-query",
    "",
  );

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [priorityCurrentPage, setPriorityCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);
  const [localPrograms, setLocalPrograms] = useState<Program[]>([]);

  const { data: programs, isLoading } = usePrograms();
  const { data: programsByPriority, isLoading: isPriorityLoading } =
    useProgramsByPriority();
  const updateProgram = useUpdateProgram();
  const toggleVisibility = useToggleVisibility();
  const bulkUpdatePriorities = useBulkUpdatePriorities();

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Sync local programs with priority data
  useEffect(() => {
    if (programsByPriority && programsByPriority.length > 0) {
      setLocalPrograms(programsByPriority);
    } else if (programs && programs.length > 0) {
      // Fallback to regular programs if priority data not loaded yet
      const sortedPrograms = [...programs].sort((a, b) => {
        const priorityA = a.priority || 999;
        const priorityB = b.priority || 999;
        return priorityA - priorityB;
      });
      setLocalPrograms(sortedPrograms);
    }
  }, [programsByPriority, programs]);

  const filteredPrograms =
    programs?.filter((program) => {
      const isIncomplete =
        !program.descriptionCustomer ||
        !program.conditions ||
        !program.cashbackRate ||
        !program.countries?.length;
      const isSetupNeeded = program.affiliateUrl && isIncomplete;

      const matchesFilter =
        filter === "all" ||
        (filter === "active" && program.visibility) ||
        (filter === "hidden" && !program.visibility) ||
        (filter === "setup-needed" && isSetupNeeded);

      const matchesSearch =
        !searchQuery ||
        program.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        program.affiliateUrl.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesFilter && matchesSearch;
    }) || [];

  // Pagination logic for regular views
  const totalItems = filteredPrograms.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPrograms = filteredPrograms.slice(startIndex, endIndex);

  // Pagination logic for priority view
  const totalPriorityItems = localPrograms.length;
  const totalPriorityPages = Math.ceil(totalPriorityItems / itemsPerPage);
  const priorityStartIndex = (priorityCurrentPage - 1) * itemsPerPage;
  const priorityEndIndex = priorityStartIndex + itemsPerPage;
  const paginatedPriorityPrograms = localPrograms.slice(
    priorityStartIndex,
    priorityEndIndex,
  );

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery, setCurrentPage]);

  // Reset priority page when filter or search changes
  React.useEffect(() => {
    setPriorityCurrentPage(1);
  }, [filter, searchQuery]);

  const handleEdit = (programId: string) => {
    const program = programs?.find((p) => p._id === programId);
    if (program) {
      setSelectedProgram(program);
      setIsFormOpen(true);
    }
  };

  const handleViewDetails = (program: Program) => {
    setSelectedProgramForDetail(program);
    setIsDetailModalOpen(true);
  };

  const handleToggleVisibility = (programId: string) => {
    toggleVisibility.mutate(programId);
  };

  const handleSaveProgram = (data: UpdateProgramData) => {
    if (selectedProgram) {
      updateProgram.mutate(
        { id: selectedProgram._id, data },
        {
          onSuccess: () => {
            setIsFormOpen(false);
            setSelectedProgram(null);
          },
        },
      );
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedProgram(null);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedProgramForDetail(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Find indices in the FULL localPrograms array
    const oldIndex = localPrograms.findIndex((p) => p._id === active.id);
    const newIndex = localPrograms.findIndex((p) => p._id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state immediately for smooth UX
    const newPrograms = arrayMove(localPrograms, oldIndex, newIndex);

    // Update priority for all programs starting from 1
    const updatedPrograms = newPrograms.map((program, index) => ({
      ...program,
      priority: index + 1,
    }));

    setLocalPrograms(updatedPrograms);

    // Send bulk update to backend
    const updates = updatedPrograms.map((program, index) => ({
      programId: program._id,
      priority: index + 1,
    }));

    bulkUpdatePriorities.mutate(updates);
  };

  const renderListView = () => {
    const isFiltered = searchQuery !== "" || filter !== "all";

    // Determine which data source to use
    // If filtered, use the filtered list (paginatedPrograms)
    // If not filtered, use the local priority list (paginatedPriorityPrograms)
    const displayPrograms = isFiltered
      ? paginatedPrograms
      : paginatedPriorityPrograms;

    // Determine pagination props
    const currentDisplayPage = isFiltered ? currentPage : priorityCurrentPage;
    const totalDisplayPages = isFiltered ? totalPages : totalPriorityPages;
    const onDisplayPageChange = isFiltered
      ? setCurrentPage
      : setPriorityCurrentPage;
    const totalDisplayItems = isFiltered ? totalItems : totalPriorityItems;

    return (
      <div className="space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          {isPriorityLoading && !isFiltered ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : displayPrograms.length > 0 ? (
            <div className="overflow-x-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {/* Drag handle column */}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Program
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cashback
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Countries
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <SortableContext
                      items={displayPrograms.map((p) => p._id)}
                      strategy={verticalListSortingStrategy}
                      disabled={isFiltered}
                    >
                      {displayPrograms.map((program, index) => {
                        // Calculate the actual index in the full localPrograms array if not filtered
                        // If filtered, we just show the priority from the program object
                        const actualIndex = isFiltered
                          ? program.priority
                            ? program.priority - 1
                            : -1
                          : localPrograms.findIndex(
                              (p) => p._id === program._id,
                            );

                        // Fallback for display index if priority is missing or we are in filtered view
                        const displayIndex =
                          actualIndex !== -1 ? actualIndex : index;

                        return (
                          <SortableProgramRow
                            key={program._id}
                            program={program}
                            index={displayIndex}
                            onEdit={handleEdit}
                            onToggleVisibility={handleToggleVisibility}
                            onRowClick={handleViewDetails}
                            isDraggable={!isFiltered}
                          />
                        );
                      })}
                    </SortableContext>
                  </tbody>
                </table>
              </DndContext>
            </div>
          ) : (
            <div className="p-12 text-center">
              <Bars3BottomLeftIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No programs found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {isFiltered
                  ? "Try adjusting your search or filters"
                  : "Sync your affiliate programs to start managing priorities"}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalDisplayPages > 1 && (
          <Pagination
            currentPage={currentDisplayPage}
            totalPages={totalDisplayPages}
            onPageChange={onDisplayPageChange}
            totalItems={totalDisplayItems}
            itemsPerPage={itemsPerPage}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Programs
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your affiliate programs
          </p>
        </div>
      </div>

      {/* Stats */}
      <ProgramStats />

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search programs by name or domain..."
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <select
              value={filter}
              onChange={(e) =>
                setFilter(
                  e.target.value as
                    | "all"
                    | "active"
                    | "hidden"
                    | "setup-needed",
                )
              }
              className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
            >
              <option value="all">All Programs</option>
              <option value="active">Active Only</option>
              <option value="hidden">Hidden Only</option>
              <option value="setup-needed">Setup Needed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Programs Content */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : filteredPrograms.length > 0 ? (
        // List view renders its own container with pagination
        renderListView()
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <FunnelIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No programs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || filter !== "all"
              ? "Try adjusting your search or filters"
              : "Start by syncing your affiliate programs"}
          </p>
        </div>
      )}

      {/* Program Form Modal */}
      <ProgramForm
        program={selectedProgram}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSave={handleSaveProgram}
        isLoading={updateProgram.isPending}
      />

      {/* Program Detail Modal */}
      <ProgramDetailModal
        program={selectedProgramForDetail}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        onEdit={handleEdit}
        onToggleVisibility={handleToggleVisibility}
      />
    </div>
  );
};

export default Programs;
